import type { ImageLoader } from 'next/image'

/**
 * imgproxy integration (off-server AVIF/WebP).
 *
 * Format is negotiated via the `Accept` header by the imgproxy/nginx layer
 * (WEBP/AVIF detection) — there is intentionally NO `f:auto` in the URL.
 *
 * This file is a self-contained, drop-in equivalent of the public API exposed by
 * `@morgendigital/next-imgproxy` (`createImgproxy`, `mediaKeyFromUrl`,
 * `imgproxy.loader`, `imgproxy.urlFromMediaUrl`). To switch to the package once it
 * can be installed (needs a GitHub token with `read:packages` + pnpm 9/10), replace
 * the body below with:
 *
 *   import { createImgproxy, mediaKeyFromUrl } from '@morgendigital/next-imgproxy'
 *   const imgproxy = createImgproxy()
 *   export const imgproxyLoader = imgproxy.loader
 *   export const imgproxyUrlFromMediaUrl = imgproxy.urlFromMediaUrl
 *   export { mediaKeyFromUrl }
 *
 * — the render sites import from `@/utilities/imgproxy` and stay untouched.
 */

export interface ImgproxyConfig {
  endpoint?: string
  bucket?: string
  quality?: number
}

const stripTrailingSlash = (s: string) => s.replace(/\/+$/, '')

const ENDPOINT = stripTrailingSlash((process.env.NEXT_PUBLIC_IMGPROXY_URL ?? '').trim())
const BUCKET = stripTrailingSlash((process.env.NEXT_PUBLIC_IMGPROXY_BUCKET ?? '').trim())
const DEFAULT_QUALITY = 80

/**
 * True only when both env vars are set (baked in at build time via NEXT_PUBLIC_*).
 * Render sites gate on this so that, when imgproxy is not configured, they fall
 * back to Next.js' on-server optimizer / plain media URLs instead of breaking.
 */
export const imgproxyEnabled = Boolean(ENDPOINT && BUCKET)

const PAYLOAD_MEDIA_MARKER = '/api/media/file/'

const isSvg = (u: string) => /\.svg(\?|#|$)/i.test(u)
const isAbsolute = (u: string) => /^https?:\/\//i.test(u)
const isDataLike = (u: string) => u.startsWith('data:') || u.startsWith('blob:')

/** Payload serves media under `/api/media/file/<key>` — detect that shape. */
export const isPayloadMediaUrl = (u: string): boolean => u.includes(PAYLOAD_MEDIA_MARKER)

/** Collapse an absolute URL to a same-origin relative path (used for SVGs). */
const toSameOrigin = (u: string): string => {
  try {
    if (isAbsolute(u)) {
      const x = new URL(u)
      return `${x.pathname}${x.search}`
    }
  } catch {
    /* noop */
  }
  return u
}

/**
 * Extract the S3 key (Payload `filename`) from a media URL.
 * Handles absolute + relative `/api/media/file/<key>` URLs and drops any query
 * string (e.g. the `?updatedAt` cache tag).
 */
export const mediaKeyFromUrl = (url?: string | null): string => {
  if (!url) return ''
  let path = url
  try {
    if (isAbsolute(url)) path = new URL(url).pathname
  } catch {
    /* noop */
  }
  path = path.split('?')[0]?.split('#')[0] ?? ''
  const idx = path.indexOf(PAYLOAD_MEDIA_MARKER)
  const seg = idx >= 0 ? path.slice(idx + PAYLOAD_MEDIA_MARKER.length) : (path.split('/').pop() ?? '')
  try {
    return decodeURIComponent(seg)
  } catch {
    return seg
  }
}

const buildUrl = (
  key: string,
  opts: { width: number; quality?: number },
  config?: ImgproxyConfig,
): string => {
  const endpoint = config?.endpoint ? stripTrailingSlash(config.endpoint) : ENDPOINT
  const bucket = config?.bucket ? stripTrailingSlash(config.bucket) : BUCKET
  const q = opts.quality ?? config?.quality ?? DEFAULT_QUALITY
  const cleanKey = key.replace(/^\/+/, '')
  return `${endpoint}/insecure/rs:fit:${opts.width}/q:${q}/plain/${bucket}/${encodeURIComponent(cleanKey)}`
}

/**
 * `next/image` loader. Attach as `loader={imgproxyEnabled ? imgproxyLoader : undefined}`.
 *
 * Deliberately conservative so it can be attached to ANY `next/image` in this
 * project without risk: it ONLY rewrites Payload media URLs (`/api/media/file/<key>`).
 * Everything else — SVGs, `data:`/`blob:` URLs, static `/public` assets, external
 * CDN URLs, `StaticImageData` src — is returned unchanged. (This codebase never
 * feeds bare S3 keys to `next/image`; it always passes full Payload media URLs, so
 * "Payload URL only" loses no coverage while eliminating false-positive keys.)
 */
export const imgproxyLoader: ImageLoader = ({ src, width, quality }) => {
  if (!src || isDataLike(src) || isSvg(src) || !isPayloadMediaUrl(src)) return src
  const key = mediaKeyFromUrl(src)
  if (!key) return src
  return buildUrl(key, { width, quality: quality ?? undefined })
}

/**
 * Build a finished imgproxy URL from a Payload media URL, for render paths that
 * cannot use a `next/image` loader (raw `<img>`, `motion.img`, WebGL textures).
 *
 * Passthrough (returns the input unchanged) when imgproxy is disabled, for SVGs
 * (never rasterize), and for non-Payload URLs (static/public/external), so it is
 * safe to wrap around expressions like `getMediaUrl(x) || '/placeholder.svg'`.
 */
export const imgproxyUrlFromMediaUrl = (
  url: string | null | undefined,
  opts: { width: number; quality?: number },
): string => {
  if (!url) return ''
  if (!imgproxyEnabled) return url
  if (isSvg(url)) return toSameOrigin(url)
  if (!isPayloadMediaUrl(url)) return url // static /public asset or external CDN
  const key = mediaKeyFromUrl(url)
  if (!key) return url
  return buildUrl(key, opts)
}

/**
 * Factory mirroring `@morgendigital/next-imgproxy`'s `createImgproxy`, for call
 * sites that need an explicitly-configured instance (custom endpoint/bucket).
 */
export const createImgproxy = (config?: ImgproxyConfig) => {
  const url = (key: string, o: { width: number; quality?: number }) => buildUrl(key, o, config)
  const loader: ImageLoader = ({ src, width, quality }) => {
    if (!src || isDataLike(src) || isSvg(src)) return src
    const payload = isPayloadMediaUrl(src)
    if (!payload && (isAbsolute(src) || src.startsWith('/'))) return src
    const key = payload ? mediaKeyFromUrl(src) : src.replace(/^\/+/, '')
    if (!key) return src
    return url(key, { width, quality: quality ?? undefined })
  }
  const urlFromMediaUrl = (u: string | null | undefined, o: { width: number; quality?: number }) => {
    if (!u) return ''
    if (isSvg(u)) return toSameOrigin(u)
    if (!isPayloadMediaUrl(u)) return u
    const key = mediaKeyFromUrl(u)
    if (!key) return u
    return url(key, o)
  }
  return { loader, url, urlFromMediaUrl }
}
